/**
 * Phase 8C — MIS P&L grid screen.
 *
 * Layout:
 *   - Header: MIS name, period picker, edit-mode toggle
 *   - Grid:
 *       Row per section (header) + sub-cat rows
 *       Columns: BUs (from Mongo) + Total
 *       Revenue cells inline-editable in edit mode; save-on-blur
 *       Expense/formula cells: placeholder 0 (Phase 8D wires actuals + formulas)
 */

import { mdiArrowLeft, mdiContentSaveEdit, mdiPencil, mdiRefresh } from "@mdi/js";
import Icon from "@mdi/react";
import {
  Fragment,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { useAuth } from "../../context_providers/auth/authProvider";
import LoadingWidget from "../../context_providers/modal/loader";
import { ToastType, useToast } from "../../context_providers/toast/toast_ctx";
import { fetchGet, fetchPut } from "../../service/api_client";
import ToggleBtn from "../../ui/new_components/common/toggle_btn";
import { ACTION, MODULE } from "../../utils/enums";
import { urls } from "../../utils/urls";
import {
  ACTUALS_SOURCE_LABEL,
  CADENCE_LABEL,
  Mis,
  MisSection,
  MisSectionType,
  MisSubCategory,
  orderToLetter,
  renderFormula,
  SECTION_TYPE_LABEL,
} from "./models/mis_model";

interface Period {
  period_start: string;
  label: string;
  months: number;
}
interface BUOption {
  id: string;
  name: string;
  code?: string;
}
interface RevenueEntry {
  id: string;
  sub_category_id: string;
  business_unit_id: string;
  period_start: string;
  amount: number;
}
interface ExpenseCell {
  sub_category_id: string;
  business_unit_id: string;
  period_start: string;
  department_id: string | null;
  amount: number;
}
interface CcInfo {
  name: string;
  code?: string;
  department_id: string | null;
}
interface DeptInfo {
  id: string;
  name: string;
}
interface FormulaCell {
  section_id: string;
  business_unit_id: string;
  period_start: string;
  amount: number;
}
interface SectionTotalCell {
  section_id: string;
  business_unit_id: string;
  period_start: string;
  amount: number;
}
interface GridPayload {
  mis: Mis;
  sections: MisSection[];
  sub_cats_by_section: Record<string, MisSubCategory[]>;
  business_units: BUOption[];
  periods: Period[];
  revenue_entries: RevenueEntry[];
  expense_cells: ExpenseCell[];
  formula_cells: FormulaCell[];
  section_totals: SectionTotalCell[];
  cc_info: Record<string, CcInfo>;
  departments: DeptInfo[];
}

const UNASSIGNED_DEPT = "__unassigned__";

const SECTION_TYPE_PILL: Record<MisSectionType, string> = {
  revenue: "bg-emerald-100 text-emerald-800",
  expense: "bg-rose-100 text-rose-800",
  formula: "bg-sky-100 text-sky-800",
};

const fmt = (n: number): string =>
  n === 0
    ? "0"
    : n.toLocaleString(undefined, {
        maximumFractionDigits: 2,
      });

/**
 * key for entry lookup: `${subCatId}::${buId}::${periodStart}`
 */
const key = (scId: string, buId: string, period: string) =>
  `${scId}::${buId}::${period}`;

const MisGridScreen: FunctionComponent = () => {
  const { id: misId } = useParams<{ id: string }>();
  const nav = useHistory();
  const baseUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/mis`;
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();

  const [data, setData] = useState<GridPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  // Per-section group-by-dept toggle. Keyed by section id.
  const [groupedSections, setGroupedSections] = useState<Record<string, boolean>>({});

  const canEdit = isAuthorised({ module: MODULE.mis, action: ACTION.UPDATE });

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchGet(`${baseUrl}/${misId}/grid`);
      if (res.success) {
        setData(res.data);
        if (!period && res.data?.periods?.length)
          setPeriod(res.data.periods[0].period_start);
      } else {
        showToast({ type: ToastType.error, text: res.error || "Load failed" });
      }
    } catch (e: any) {
      showToast({ type: ToastType.error, text: e?.message || "Network error" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, misId]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  // Build fast lookups.
  //   revenueMap:   (subCat, BU, period) → amount (from stored entries)
  //   expenseMap:   (subCat, BU, period) → amount (backend-computed from CC actuals)
  //   sectionMap:   (section, BU, period) → total (backend-computed section total)
  //   formulaMap:   (section, BU, period) → amount (backend-computed formula result)
  const revenueMap = useMemo(() => {
    const m: Record<string, number> = {};
    if (!data) return m;
    for (const e of data.revenue_entries) {
      // period_start arrives as full ISO ("2026-04-01T00:00:00.000Z") from PG;
      // dropdown + computed arrays use 10-char form. Normalise.
      const p = String(e.period_start).slice(0, 10);
      m[key(e.sub_category_id, e.business_unit_id, p)] = Number(e.amount);
    }
    return m;
  }, [data]);

  // Flat expense map: sum across departments per (subCat, BU, period).
  const expenseMap = useMemo(() => {
    const m: Record<string, number> = {};
    if (!data) return m;
    for (const e of data.expense_cells || []) {
      const k = key(e.sub_category_id, e.business_unit_id, e.period_start);
      m[k] = (m[k] ?? 0) + Number(e.amount);
    }
    return m;
  }, [data]);

  // Dept-scoped expense map: keyed by (subCat, BU, period, dept).
  const deptExpenseMap = useMemo(() => {
    const m: Record<string, number> = {};
    if (!data) return m;
    for (const e of data.expense_cells || []) {
      const dept = e.department_id ?? UNASSIGNED_DEPT;
      const k = `${e.sub_category_id}::${e.business_unit_id}::${e.period_start}::${dept}`;
      m[k] = (m[k] ?? 0) + Number(e.amount);
    }
    return m;
  }, [data]);

  const deptName = (deptId: string): string => {
    if (deptId === UNASSIGNED_DEPT) return "Unassigned";
    return data?.departments.find((d) => d.id === deptId)?.name || deptId;
  };

  const sectionTotalMap = useMemo(() => {
    const m: Record<string, number> = {};
    if (!data) return m;
    for (const e of data.section_totals || []) {
      m[key(e.section_id, e.business_unit_id, e.period_start)] = Number(
        e.amount,
      );
    }
    return m;
  }, [data]);

  const formulaMap = useMemo(() => {
    const m: Record<string, number> = {};
    if (!data) return m;
    for (const e of data.formula_cells || []) {
      m[key(e.section_id, e.business_unit_id, e.period_start)] = Number(
        e.amount,
      );
    }
    return m;
  }, [data]);

  const saveEntry = async (
    scId: string,
    buId: string,
    p: string,
    amount: number,
  ) => {
    // Optimistically update revenue map so the cell doesn't visually reset
    // while the round-trip is in flight.
    if (data) {
      const idx = data.revenue_entries.findIndex(
        (e) =>
          e.sub_category_id === scId &&
          e.business_unit_id === buId &&
          String(e.period_start).slice(0, 10) === p,
      );
      const updated = [...data.revenue_entries];
      if (idx >= 0) updated[idx] = { ...updated[idx], amount };
      else
        updated.push({
          id: `local-${Date.now()}`,
          sub_category_id: scId,
          business_unit_id: buId,
          period_start: p,
          amount,
        });
      setData({ ...data, revenue_entries: updated });
    }
    const res = await fetchPut(`${baseUrl}/${misId}/entry`, {
      sub_category_id: scId,
      business_unit_id: buId,
      period_start: p,
      amount,
    });
    if (!res.success) {
      showToast({ type: ToastType.error, text: res.error || "Save failed" });
      // Roll back by re-fetching.
      fetchGrid();
      return;
    }
    showToast({ type: ToastType.success, text: "Saved" });
    // Background refresh so formulas / section totals pick up the new value.
    fetchGrid();
  };

  /** Cell amount for a sub-cat × BU × period, respecting section type. */
  const cellValue = (
    section: MisSection,
    scId: string,
    buId: string,
    p: string,
  ): number => {
    if (section.section_type === "revenue")
      return revenueMap[key(scId, buId, p)] ?? 0;
    if (section.section_type === "expense")
      return expenseMap[key(scId, buId, p)] ?? 0;
    return 0;
  };

  /** Row total for a sub-cat across BUs in the current period. */
  const subCatRowTotal = (section: MisSection, scId: string): number => {
    if (!data || !period) return 0;
    return data.business_units.reduce(
      (sum, bu) => sum + cellValue(section, scId, bu.id, period),
      0,
    );
  };

  /** Section total per BU (from backend). Falls back to 0. */
  const sectionBUTotal = (section: MisSection, buId: string): number => {
    if (!period) return 0;
    if (section.section_type === "formula")
      return formulaMap[key(section.id, buId, period)] ?? 0;
    return sectionTotalMap[key(section.id, buId, period)] ?? 0;
  };

  const sectionRowTotal = (section: MisSection): number => {
    if (!data) return 0;
    return data.business_units.reduce(
      (sum, bu) => sum + sectionBUTotal(section, bu.id),
      0,
    );
  };

  /** Percentage-row value for a section, per BU. (this / percent_of) × 100. */
  const percentageBU = (section: MisSection, buId: string): number => {
    if (!section.show_percentage || !section.percentage_of_section_id)
      return 0;
    const denom = sectionTotalMap[
      key(section.percentage_of_section_id, buId, period || "")
    ];
    if (!denom) return 0;
    return (sectionBUTotal(section, buId) / denom) * 100;
  };

  const percentageRowTotal = (section: MisSection): number => {
    if (!section.show_percentage || !section.percentage_of_section_id)
      return 0;
    if (!data) return 0;
    const denomTotal = data.business_units.reduce(
      (sum, bu) =>
        sum +
        (sectionTotalMap[
          key(section.percentage_of_section_id!, bu.id, period || "")
        ] ?? 0),
      0,
    );
    if (!denomTotal) return 0;
    return (sectionRowTotal(section) / denomTotal) * 100;
  };

  const fmtPct = (n: number): string =>
    Number.isFinite(n) ? `${n.toFixed(2)}%` : "—";

  // ---- Dept-grouping helpers (Phase 8D+) ---------------------------------

  /** All departments touched by an expense section's CCs. */
  const deptsInSection = (sec: MisSection): string[] => {
    if (!data || sec.section_type !== "expense") return [];
    const scs = data.sub_cats_by_section[sec.id] || [];
    const set = new Set<string>();
    for (const sc of scs) {
      for (const ccId of sc.cost_center_ids || []) {
        const d = data.cc_info[ccId]?.department_id ?? UNASSIGNED_DEPT;
        set.add(d);
      }
    }
    return [...set].sort((a, b) => deptName(a).localeCompare(deptName(b)));
  };

  /** Sub-cats that have at least one CC belonging to `deptId`. */
  const subCatsInDept = (
    sec: MisSection,
    deptId: string,
  ): MisSubCategory[] => {
    if (!data) return [];
    const scs = data.sub_cats_by_section[sec.id] || [];
    return scs.filter((sc) =>
      (sc.cost_center_ids || []).some(
        (ccId) =>
          (data.cc_info[ccId]?.department_id ?? UNASSIGNED_DEPT) === deptId,
      ),
    );
  };

  /** CC ids for a sub-cat, optionally filtered to a specific dept. */
  const ccIdsForSubCat = (
    sc: MisSubCategory,
    deptId: string | null,
  ): string[] => {
    if (!data) return sc.cost_center_ids || [];
    if (deptId === null) return sc.cost_center_ids || [];
    return (sc.cost_center_ids || []).filter(
      (ccId) =>
        (data.cc_info[ccId]?.department_id ?? UNASSIGNED_DEPT) === deptId,
    );
  };

  /** Cell value optionally scoped to a dept (only meaningful for expense). */
  const cellValueScoped = (
    sec: MisSection,
    scId: string,
    buId: string,
    p: string,
    deptId: string | null,
  ): number => {
    if (sec.section_type !== "expense" || deptId === null)
      return cellValue(sec, scId, buId, p);
    return deptExpenseMap[`${scId}::${buId}::${p}::${deptId}`] ?? 0;
  };

  const subCatRowTotalScoped = (
    sec: MisSection,
    scId: string,
    deptId: string | null,
  ): number => {
    if (!data || !period) return 0;
    return data.business_units.reduce(
      (sum, bu) => sum + cellValueScoped(sec, scId, bu.id, period, deptId),
      0,
    );
  };

  /** Human label for the percentage row: "CM1 %" style. */
  const pctLabel = (section: MisSection): string => {
    const base = section.title.replace(/\s*\(.*\)\s*$/, ""); // strip trailing "(a - b)"
    return `${base.trim()} %`;
  };

  if (loading && !data)
    return (
      <div className="p-6">
        <LoadingWidget />
      </div>
    );
  if (!data) return null;

  const bus = data.business_units;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <button
            onClick={() => nav.push(`${urls.mis_view}/${misId}`)}
            className="text-xs text-myPrimaryColor hover:underline mb-1 flex items-center gap-1"
          >
            <Icon path={mdiArrowLeft} className="h-3 w-3" /> Back to template
          </button>
          <h1 className="text-2xl font-bold text-gray-800">{data.mis.name}</h1>
          <div className="mt-1 flex gap-2 flex-wrap text-xs text-gray-600">
            <span className="border border-gray-300 rounded px-2 py-0.5">
              {data.mis.start_fy}
            </span>
            <span className="border border-gray-300 rounded px-2 py-0.5">
              {CADENCE_LABEL[data.mis.cadence_months]}
            </span>
            <span className="border border-gray-300 rounded px-2 py-0.5">
              {ACTUALS_SOURCE_LABEL[data.mis.actuals_source]}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={period ?? ""}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm bg-white"
          >
            {data.periods.map((p) => (
              <option key={p.period_start} value={p.period_start}>
                {p.label}
              </option>
            ))}
          </select>
          <button
            onClick={fetchGrid}
            className="border border-gray-300 rounded px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-1"
            title="Refresh"
          >
            <Icon path={mdiRefresh} className="h-4 w-4" />
          </button>
          {canEdit && (
            <button
              onClick={() => setEditMode((v) => !v)}
              className={`rounded px-3 py-2 text-sm font-semibold flex items-center gap-1 ${
                editMode
                  ? "bg-amber-500 hover:bg-amber-600 text-white"
                  : "bg-myPrimaryColor hover:bg-mySecondaryColor text-white"
              }`}
            >
              <Icon
                path={editMode ? mdiContentSaveEdit : mdiPencil}
                className="h-4 w-4"
              />
              {editMode ? "Done editing" : "Edit revenue"}
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="overflow-auto bg-white border border-gray-200 rounded-lg">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="sticky left-0 z-10 bg-gray-50 text-left px-3 py-2 border-r border-gray-200 min-w-[280px]">
                Particulars
              </th>
              {bus.map((bu) => (
                <th
                  key={bu.id}
                  className="text-right px-3 py-2 border-r border-gray-200 min-w-[120px]"
                >
                  <div className="font-semibold text-gray-800">{bu.name}</div>
                  {bu.code && (
                    <div className="text-[10px] text-gray-500 font-normal">
                      {bu.code}
                    </div>
                  )}
                </th>
              ))}
              <th className="text-right px-3 py-2 min-w-[120px] bg-gray-100 font-bold">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {data.sections.map((sec) => {
              const isFormula = sec.section_type === "formula";
              const scs = isFormula ? [] : data.sub_cats_by_section[sec.id] || [];
              return (
                <Fragment key={sec.id}>
                  {/* Section header row */}
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="sticky left-0 z-10 bg-gray-50 px-3 py-2 border-r border-gray-200">
                      <div className="flex items-center gap-2 flex-wrap w-full">
                        <span className="font-mono font-bold text-gray-600">
                          {orderToLetter(sec.display_order)}.
                        </span>
                        <span className="font-bold text-gray-800">
                          {sec.title}
                        </span>
                        <span
                          className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${SECTION_TYPE_PILL[sec.section_type]}`}
                        >
                          {SECTION_TYPE_LABEL[sec.section_type]}
                        </span>
                        {isFormula && sec.formula_expression && (
                          <span className="text-xs text-gray-500 font-mono">
                            ={" "}
                            {renderFormula(
                              sec.formula_expression,
                              Object.fromEntries(
                                data.sections.map((s) => [s.id, s.display_order]),
                              ),
                            )}
                          </span>
                        )}
                        {sec.section_type === "expense" && (
                          <div className="ml-auto flex items-center gap-2">
                            <span className="text-xs text-gray-600">
                              Group by Dept
                            </span>
                            <ToggleBtn
                              neutral
                              value={!!groupedSections[sec.id]}
                              onChange={(v) =>
                                setGroupedSections((prev) => ({
                                  ...prev,
                                  [sec.id]: v,
                                }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    {bus.map((bu) => (
                      <td
                        key={bu.id}
                        className={`text-right px-3 py-2 border-r border-gray-200 font-semibold ${
                          isFormula ? "text-sky-800 italic" : "text-gray-800"
                        }`}
                      >
                        {fmt(sectionBUTotal(sec, bu.id))}
                      </td>
                    ))}
                    <td
                      className={`text-right px-3 py-2 bg-gray-100 font-bold ${
                        isFormula ? "text-sky-800 italic" : ""
                      }`}
                    >
                      {fmt(sectionRowTotal(sec))}
                    </td>
                  </tr>

                  {/* Percentage row — appears right under the section header
                      when show_percentage=true and percentage_of_section_id
                      is set. Applies to any section type (revenue/expense/formula). */}
                  {sec.show_percentage && sec.percentage_of_section_id && (
                    <tr className="border-b border-gray-100 bg-sky-50">
                      <td className="sticky left-0 z-10 bg-sky-50 px-3 py-1.5 pl-10 border-r border-gray-200 italic text-sky-800">
                        {pctLabel(sec)}
                      </td>
                      {bus.map((bu) => (
                        <td
                          key={bu.id}
                          className="text-right px-3 py-1.5 border-r border-gray-200 italic text-sky-800"
                        >
                          {fmtPct(percentageBU(sec, bu.id))}
                        </td>
                      ))}
                      <td className="text-right px-3 py-1.5 bg-sky-100 italic font-semibold text-sky-800">
                        {fmtPct(percentageRowTotal(sec))}
                      </td>
                    </tr>
                  )}

                  {/* Sub-category rows (non-formula only) */}
                  {(() => {
                    const isExpense = sec.section_type === "expense";
                    const renderRow = (
                      sc: MisSubCategory,
                      deptId: string | null,
                      rowKeyPrefix: string,
                    ) => {
                      const isRevenue = sec.section_type === "revenue";
                      const isExp = sec.section_type === "expense";
                      const ccIds = isExp
                        ? ccIdsForSubCat(sc, deptId)
                        : [];
                      return (
                        <tr
                          key={`${rowKeyPrefix}-${sc.id}`}
                          className="border-b border-gray-100 hover:bg-gray-50"
                        >
                          <td className="sticky left-0 z-10 bg-white hover:bg-gray-50 px-3 py-1.5 pl-10 border-r border-gray-200 text-gray-700">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span>{sc.label}</span>
                              {ccIds.map((ccId) => {
                                const info = data.cc_info[ccId];
                                if (!info) return null;
                                return (
                                  <span
                                    key={ccId}
                                    className="inline-flex items-center rounded bg-gray-100 border border-gray-300 text-gray-600 px-1.5 py-0 text-[10px] font-medium"
                                    title={info.code || info.name}
                                  >
                                    {info.name}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          {bus.map((bu) => {
                            const cellVal = cellValueScoped(
                              sec,
                              sc.id,
                              bu.id,
                              period || "",
                              deptId,
                            );
                            return (
                              <td
                                key={bu.id}
                                className={`text-right px-2 py-1 border-r border-gray-200 ${
                                  isExp ? "text-rose-800" : ""
                                }`}
                              >
                                {isRevenue && editMode && period ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    defaultValue={cellVal}
                                    onBlur={(e) => {
                                      const v = Number(e.target.value || 0);
                                      if (v !== cellVal)
                                        saveEntry(sc.id, bu.id, period, v);
                                    }}
                                    className="w-full text-right text-sm px-2 py-1 rounded border border-transparent hover:border-gray-300 focus:border-myPrimaryColor focus:outline-none"
                                  />
                                ) : (
                                  <span
                                    className={isExp ? "" : "text-gray-700"}
                                  >
                                    {fmt(cellVal)}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                          <td
                            className={`text-right px-3 py-1.5 bg-gray-50 font-semibold ${
                              isExp ? "text-rose-800" : "text-gray-700"
                            }`}
                          >
                            {fmt(subCatRowTotalScoped(sec, sc.id, deptId))}
                          </td>
                        </tr>
                      );
                    };

                    if (groupedSections[sec.id] && isExpense) {
                      const groups = deptsInSection(sec);
                      return groups.map((deptId) => {
                        const scsInDept = subCatsInDept(sec, deptId);
                        // Dept-group total per BU = sum of member sub-cats.
                        const deptBUTotal = (buId: string): number =>
                          scsInDept.reduce(
                            (sum, sc) =>
                              sum +
                              cellValueScoped(
                                sec,
                                sc.id,
                                buId,
                                period || "",
                                deptId,
                              ),
                            0,
                          );
                        const deptRowTotal = bus.reduce(
                          (sum, bu) => sum + deptBUTotal(bu.id),
                          0,
                        );
                        return (
                          <Fragment key={`dept-${sec.id}-${deptId}`}>
                            <tr className="border-b border-gray-100 bg-gray-100">
                              <td className="sticky left-0 z-10 bg-gray-100 px-3 py-1.5 pl-8 border-r border-gray-200 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                                {deptName(deptId)}
                              </td>
                              {bus.map((bu) => (
                                <td
                                  key={bu.id}
                                  className="text-right px-3 py-1.5 border-r border-gray-200 text-rose-800 font-semibold text-xs"
                                >
                                  {fmt(deptBUTotal(bu.id))}
                                </td>
                              ))}
                              <td className="text-right px-3 py-1.5 bg-gray-200 text-rose-800 font-bold text-xs">
                                {fmt(deptRowTotal)}
                              </td>
                            </tr>
                            {scsInDept.map((sc) =>
                              renderRow(sc, deptId, `d-${deptId}`),
                            )}
                          </Fragment>
                        );
                      });
                    }
                    return scs.map((sc) => renderRow(sc, null, "flat"));
                  })()}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footnote */}
      <p className="mt-3 text-xs text-gray-500">
        Revenue: stored per (sub-cat × BU × period) — editable.
        {" · "}
        Expense: aggregated from Mongo CC actuals (
        {ACTUALS_SOURCE_LABEL[data.mis.actuals_source]}), read-only.
        {" · "}
        Formula: evaluated against section totals for the same (BU × period).
      </p>
    </div>
  );
};

export default MisGridScreen;
